import { useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { streamPlayground } from "@/lib/mh";
import { useAuth } from "@/context/AuthContext";
import { Panel, Eyebrow } from "@/components/mh/bits";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const Playground = ({ agent }) => {
  const { canManage } = useAuth();
  const [manual, setManual] = useState("0");
  const [input, setInput] = useState("");
  const [out, setOut] = useState("");
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const run = async () => {
    setBusy(true); setOut(""); setErr(""); setMeta(null);
    try {
      await streamPlayground(agent.slug, { manual_index: Number(manual), input }, (ev) => {
        if (ev.type === "meta") setMeta(ev);
        if (ev.type === "delta") setOut((o) => o + ev.text);
        if (ev.type === "error") setErr(ev.error);
      });
    } catch (e) {
      setErr(e.message);
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel cls="p-5 space-y-4" testId="agent-playground">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><Eyebrow>Try this agent</Eyebrow><h3 className="font-display text-2xl mt-1">Playground</h3></div>
        {meta && <span className="font-mono text-[10px] text-muted-foreground" data-testid="playground-meta">{meta.source === "own" ? "client key" : "managed"} · {meta.provider} · {meta.model}</span>}
      </div>
      <div className="grid sm:grid-cols-[220px_1fr] gap-3">
        <Select value={manual} onValueChange={setManual}>
          <SelectTrigger className="h-10 rounded-lg bg-white/[0.03] border-white/10" data-testid="playground-manual"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0A0A0F] border-white/10">{(agent.prompt_manuals || []).map((p, i) => <SelectItem key={p.name} value={String(i)}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
        <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={3} placeholder="Paste a sample input: a lead list, a ticket, an email thread…" data-testid="playground-input"
          className="w-full px-3.5 py-2.5 rounded-lg bg-white/[0.03] border border-white/10 text-sm outline-none focus:border-[#D4FF3A]/60 resize-y" />
      </div>
      <button onClick={run} disabled={busy || !input.trim() || !canManage} data-testid="playground-run"
        className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-[#D4FF3A] text-black text-sm font-semibold hover:bg-[#e2ff73] transition-colors disabled:opacity-40">
        <Sparkles className="w-4 h-4" /> {busy ? "Thinking…" : canManage ? "Run in playground" : "Managers only"}
      </button>
      {err && <p className="text-sm text-[#FF3B5C]" data-testid="playground-error">{err}</p>}
      {(out || busy) && <pre className="whitespace-pre-wrap text-sm leading-relaxed max-h-[460px] overflow-auto rounded-lg bg-black/40 border border-white/[0.06] p-4 font-sans" data-testid="playground-output">{out}{busy && <span className="mh-blink">▍</span>}</pre>}
    </Panel>
  );
};
